import Button from "components/button";
import { ConfirmModal } from "components/confirmModal";
import { Loader } from "components/loader";
import { useNavigate } from "react-router-dom";

interface ImportStatusModalProps {
  open: boolean;
  uploading: boolean;
  processing: boolean;
  error: string;
  pollError: string;
  result: { status: "processing" | "success" | "error"; message?: string | null } | null;
  fileLink: string;
  onCheckAgain: () => void;
  onClose: () => void;
}

export const ImportStatusModal = ({ open, uploading, processing, error, pollError, result, fileLink, onCheckAgain, onClose }: ImportStatusModalProps) => {
  const navigate = useNavigate();
  const busy = uploading || processing;
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <ConfirmModal
      open={open}
      title="Pickup account import"
      titleInHeader
      content=""
      hideActions={busy}
      closeOnClickBackdrop={false}
      onClose={close}
      onConfirm={() => { if (!busy) navigate("/pickup/accounts"); }}
      footer={<>
        <Button onClick={close} className="w-full bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">Close</Button>
        <Button onClick={() => { if (!busy) navigate("/pickup/accounts"); }} className="w-full bg-brand-500 text-white">Account list</Button>
      </>}
      customContent={(
        <div className="space-y-3 text-sm text-left" aria-live="polite">
          {busy && !pollError && <div className="flex items-center gap-3" role="status">
            <Loader size={28} fillColors={["#011BB6", "#011BB6", "#011BB6"]} aria-hidden="true" />
            <p>{uploading ? "Uploading file…" : "Processing import. Please wait for the result."}</p>
          </div>}
          {error && <p role="alert" className="error-text">{error}</p>}
          {pollError && <div role="alert">
            <p className="error-text">{pollError}</p>
            <p className="mt-2">The import may still be running. Check its result before uploading again.</p>
            <Button className="mt-3" onClick={onCheckAgain}>Check again</Button>
          </div>}
          {result?.status === "success" && <p role="status" className="text-green-700">Pickup accounts imported successfully.</p>}
          {result?.status === "error" && <div role="alert">
            <p className="error-text">{result.message || "Some rows could not be imported."}</p>
            {fileLink ? <>
              <p className="mt-2">Download the result file and review the issue column for errors on each row. The download link expires after 1 hour.</p>
              <a href={fileLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-brand-500 underline">Download result file</a>
            </> : <p className="mt-2">No result file was provided.</p>}
          </div>}
        </div>
      )}
    />
  );
};
